import React from 'react';
import { Box, Typography } from '@mui/material';
import ProjectLandLotMap from './detail/map/ProjectLandLotMap';

const THUMBNAIL_HEIGHT = 200;

function hasMapCoordinates(
  latitude: number | null | undefined,
  longitude: number | null | undefined,
): boolean {
  return (
    latitude != null &&
    longitude != null &&
    Number.isFinite(Number(latitude)) &&
    Number.isFinite(Number(longitude))
  );
}

export interface SitePlanCardThumbnailProps {
  projectName: string;
  imageUrl?: string | null;
  mapLatitude?: number | null;
  mapLongitude?: number | null;
  /** Upcoming cards inset the thumbnail inside the animated border. */
  upcoming?: boolean;
}

const SitePlanCardThumbnail: React.FC<SitePlanCardThumbnailProps> = ({
  projectName,
  imageUrl,
  mapLatitude,
  mapLongitude,
  upcoming = false,
}) => {
  const frameSx = {
    width: upcoming ? 'calc(100% - 8px)' : '100%',
    height: THUMBNAIL_HEIGHT,
    ...(upcoming && {
      m: '4px',
      borderRadius: '4px',
    }),
  };

  if (imageUrl) {
    return (
      <Box
        component="img"
        src={imageUrl}
        alt={projectName}
        loading="lazy"
        sx={{
          ...frameSx,
          objectFit: 'cover',
          bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
          display: 'block',
        }}
      />
    );
  }

  if (hasMapCoordinates(mapLatitude, mapLongitude)) {
    return (
      <Box sx={{ ...frameSx, overflow: 'hidden', bgcolor: 'rgba(var(--color-text-rgb), 0.05)' }}>
        <ProjectLandLotMap
          latitude={Number(mapLatitude)}
          longitude={Number(mapLongitude)}
          projectName={projectName}
          height={THUMBNAIL_HEIGHT}
          showLotLabel={false}
          interactive={false}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...frameSx,
        bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }}>
        Site Plan Not Available
      </Typography>
    </Box>
  );
};

export default SitePlanCardThumbnail;
